import { useCallback, useEffect, useState } from "react";

export const SFARM_THEME_STORAGE_KEY = "smart_farm_theme";
export const SFARM_THEME_ATTRIBUTE = "data-sfarm-theme";
export const SFARM_THEME_EVENT = "sfarm-theme-change";

export const SFARM_THEMES = [
  {
    key: "default",
    label: "گلخانه هوشمند",
    description: "تم اصلی سیستم",
    swatchClass: "themeSwatchDefault",
  },
  {
    key: "smart-home",
    label: "خانه هوشمند",
    description: "فضای تیره با المان‌های نئونی",
    swatchClass: "themeSwatchSmartHome",
  },
  {
    key: "industrial-blue",
    label: "صنعتی آبی",
    description: "محیط صنعتی و داشبورد شیشه‌ای",
    swatchClass: "themeSwatchIndustrial",
  },
  {
    key: "home-soft",
    label: "خانه روشن",
    description: "فضای روشن و نرم خانگی",
    swatchClass: "themeSwatchHomeSoft",
  },
];

const isBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

export const normalizeThemeKey = (themeKey) => {
  const candidate = String(themeKey || "").trim();

  if (SFARM_THEMES.some((theme) => theme.key === candidate)) {
    return candidate;
  }

  return "default";
};

export const getStoredThemeKey = () => {
  if (!isBrowser()) return "default";

  try {
    return normalizeThemeKey(
      window.localStorage.getItem(SFARM_THEME_STORAGE_KEY),
    );
  } catch (error) {
    console.error("خطا در خواندن تم ذخیره‌شده:", error);
    return "default";
  }
};

export const applyLocalTheme = (themeKey, options = {}) => {
  if (!isBrowser()) return "default";

  const normalizedThemeKey = normalizeThemeKey(themeKey);
  const shouldPersist = options.persist !== false;
  const shouldNotify = options.notify !== false;

  document.documentElement.setAttribute(
    SFARM_THEME_ATTRIBUTE,
    normalizedThemeKey,
  );

  try {
    if (shouldPersist) {
      window.localStorage.setItem(SFARM_THEME_STORAGE_KEY, normalizedThemeKey);
    }
  } catch (error) {
    console.error("خطا در ذخیره تم:", error);
  }

  if (shouldNotify) {
    window.dispatchEvent(
      new CustomEvent(SFARM_THEME_EVENT, {
        detail: { themeKey: normalizedThemeKey },
      }),
    );
  }

  return normalizedThemeKey;
};

export const initLocalTheme = () => {
  const storedThemeKey = getStoredThemeKey();
  return applyLocalTheme(storedThemeKey, { persist: false, notify: false });
};

export const useLocalTheme = () => {
  const [themeKey, setThemeKey] = useState(() => getStoredThemeKey());

  useEffect(() => {
    const initialThemeKey = initLocalTheme();
    setThemeKey(initialThemeKey);

    const handleThemeChange = (event) => {
      setThemeKey(normalizeThemeKey(event?.detail?.themeKey));
    };

    const handleStorage = (event) => {
      if (event.key === SFARM_THEME_STORAGE_KEY) {
        const nextThemeKey = normalizeThemeKey(event.newValue);
        applyLocalTheme(nextThemeKey, { persist: false, notify: false });
        setThemeKey(nextThemeKey);
      }
    };

    window.addEventListener(SFARM_THEME_EVENT, handleThemeChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(SFARM_THEME_EVENT, handleThemeChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const setTheme = useCallback((nextThemeKey) => {
    const appliedThemeKey = applyLocalTheme(nextThemeKey);
    setThemeKey(appliedThemeKey);
    return appliedThemeKey;
  }, []);

  const theme =
    SFARM_THEMES.find((item) => item.key === themeKey) || SFARM_THEMES[0];

  return {
    themeKey,
    theme,
    themes: SFARM_THEMES,
    setTheme,
  };
};
